/**
 * Tag component for the Indigo design system.
 *
 * A versatile tag/chip component that can be used to display status, categories, or labels.
 * Supports multiple visual styles and sizes to accommodate different use cases.
 *
 * Features:
 * - Two size variants: small (9px) and medium (11px)
 * - Outlined variant with border (with/without background)
 * - Solid variant for status indicators (success/error/warning/neutral)
 * - Four status types: success, error, warning, neutral
 * - Consistent spacing and border radius
 * - Uses design system colors and tokens
 *
 * @example
 * ```tsx
 * // Small outlined tag
 * <Tag label="Draft" variant="outlined" size="small" />
 *
 * // Medium outlined tag with background
 * <Tag label="In Progress" variant="outlined" size="medium" />
 *
 * // Outlined tag with error status
 * <Tag label="Error" variant="outlined" status="error" size="small" />
 *
 * // Outlined tag with neutral status
 * <Tag label="Inactive" variant="outlined" status="neutral" size="small" />
 *
 * // Small solid success tag
 * <Tag label="Active" variant="solid" status="success" size="small" />
 *
 * // Small solid error tag
 * <Tag label="Failed" variant="solid" status="error" size="small" />
 *
 * // Small solid warning tag
 * <Tag label="Pending" variant="solid" status="warning" size="small" />
 *
 * // Small solid neutral tag
 * <Tag label="N/A" variant="solid" status="neutral" size="small" />
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import "./Tag.css";

const tagVariants = cva("indigo-tag", {
  variants: {
    variant: {
      outlined: "",
      solid: "",
      filled: "",
    },
    size: {
      small: "small",
      compact: "compact",
      medium: "medium",
    },
    shape: {
      pill: "",
      square: "square",
    },
    status: {
      success: "",
      error: "",
      warning: "",
      neutral: "",
      info: "",
    },
  },
  compoundVariants: [
    {
      variant: "outlined",
      size: "small",
      className: "outlined-small",
    },
    {
      variant: "outlined",
      size: "medium",
      className: "outlined-medium",
    },
    {
      variant: "outlined",
      size: "small",
      status: "error",
      className: "outlined-small-error",
    },
    {
      variant: "outlined",
      size: "medium",
      status: "error",
      className: "outlined-medium-error",
    },
    {
      variant: "outlined",
      size: "small",
      status: "warning",
      className: "outlined-small-warning",
    },
    {
      variant: "outlined",
      size: "medium",
      status: "warning",
      className: "outlined-medium-warning",
    },
    {
      variant: "solid",
      status: "success",
      className: "solid-success",
    },
    {
      variant: "solid",
      status: "error",
      className: "solid-error",
    },
    {
      variant: "solid",
      status: "warning",
      className: "solid-warning",
    },
    {
      variant: "solid",
      status: "neutral",
      className: "solid-neutral",
    },
    {
      variant: "outlined",
      size: "small",
      status: "neutral",
      className: "outlined-small-neutral",
    },
    {
      variant: "outlined",
      size: "medium",
      status: "neutral",
      className: "outlined-medium-neutral",
    },
    {
      variant: "solid",
      status: "info",
      className: "solid-info",
    },
    {
      variant: "outlined",
      size: "small",
      status: "info",
      className: "outlined-small-info",
    },
    {
      variant: "outlined",
      size: "medium",
      status: "info",
      className: "outlined-medium-info",
    },
    {
      variant: "filled",
      status: "success",
      className: "filled-success",
    },
    {
      variant: "filled",
      status: "error",
      className: "filled-error",
    },
    {
      variant: "filled",
      status: "warning",
      className: "filled-warning",
    },
    {
      variant: "filled",
      status: "neutral",
      className: "filled-neutral",
    },
    {
      variant: "filled",
      status: "info",
      className: "filled-info",
    },
  ],
  defaultVariants: {
    variant: "outlined",
    size: "small",
    shape: "pill",
  },
});

export interface TagProps extends VariantProps<typeof tagVariants> {
  /**
   * The text content of the tag
   */
  label: string;

  /**
   * Optional icon element to render before the label
   */
  icon?: React.ReactNode;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test ID for the component
   */
  "data-testid"?: string;
}

/**
 * Tag component
 *
 * @param props - Component props (@see TagProps)
 * @returns A React component for displaying a tag/chip
 */
export const Tag = ({
  label,
  variant,
  size,
  shape,
  status,
  icon,
  className,
  "data-testid": dataTestId,
}: TagProps) => {
  return (
    <div
      className={tagVariants({ variant, size, shape, status, className })}
      data-testid={dataTestId}
    >
      {icon && <span className="indigo-tag__icon">{icon}</span>}
      {label}
    </div>
  );
};

export { tagVariants };
export default Tag;
